import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loading, PageHeader } from "../../components/shared";

export default function AdminSocietyOverview() {
  const { societyId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/society-setup/admin/societies/${societyId}/overview`)
      .then((response) => setData(response.data))
      .catch((error) => {
        toast.error(error.response?.data?.detail || "Could not load society overview");
        setData({});
      });
  }, [societyId]);

  if (!data) return <Loading />;
  if (!data.society) return <div data-testid="admin-society-overview-error">Unavailable</div>;

  const { society, setup, commercial_subscription: subscription, application } = data;
  return (
    <div data-testid="admin-society-overview-page">
      <PageHeader
        title={society.name || "Society overview"}
        subtitle="Platform read-only overview. Society data remains tenant-scoped."
      >
        <Link data-testid="admin-society-back-link" to="/admin/societies">
          <Button variant="outline">Back to societies</Button>
        </Link>
      </PageHeader>

      <section className="grid gap-4 md:grid-cols-3">
        <Info label="Operational status" value={society.operational_status || "SETUP"} />
        <Info label="Commercial subscription" value={subscription?.status || "Not configured"} />
        <Info label="Application" value={application?.status || "Not linked"} />
      </section>

      <section className="mt-7 grid gap-4 md:grid-cols-4">
        <Info label="Buildings" value={setup.buildings} />
        <Info label="Units" value={setup.units} />
        <Info label="Committee terms" value={setup.current_committees} />
        <Info label="Documents" value={setup.documents} />
      </section>

      <section className="mt-7 grid gap-5 lg:grid-cols-2">
        <article className="border bg-card p-5">
          <h2 className="font-display font-bold">Profile</h2>
          <dl className="mt-4 grid gap-3 text-sm">
            <DataRow label="Registration" value={society.registration_number} />
            <DataRow label="Address" value={society.address} />
            <DataRow label="Contact" value={society.primary_contact} />
            <DataRow label="Email" value={society.official_email} />
          </dl>
        </article>
        <article className="border bg-card p-5">
          <h2 className="font-display font-bold">Linked source records</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            These links open existing source modules; no record is duplicated here.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {application && (
              <Link data-testid="admin-society-application-link" to="/admin/applications">
                <Button variant="outline">
                  Application
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </Link>
            )}
            <Link data-testid="admin-society-commercial-link" to="/admin/commercial">
              <Button variant="outline">
                Commercial Control
                <ExternalLink className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </article>
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border bg-card p-4">
      <div className="font-display text-xl font-bold">{value ?? 0}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function DataRow({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 font-medium">{value || "—"}</dd>
    </div>
  );
}